/**
 * feat-299 — apply one saved edit to the in-memory `learn-content` tree, so the
 * page shows the new value without refetching the whole curriculum per save
 * (slice deliverable 5).
 *
 * Pure functions over `QuizContent`. They patch ENGLISH (canonical) columns
 * only and leave every `translations` overlay exactly as loaded: the translated
 * cell is not touched after an English save, and the `Outdated` chip appears on
 * the next load through the existing mechanism (spec §4, NOTES §10). Faking it
 * client-side would be a client twin of a server decision.
 *
 * Unchanged branches keep their identity, so React only re-renders what moved.
 */
import {
  QuizContent,
  QuizLesson,
  QuizQuestion,
  QuizUnit,
  ReadingItemSummary,
  RehearsalCriterion,
  RehearsalLessonItem,
} from '../../../services/quizContentAdminService';

function mapDrills(content: QuizContent, fn: (d: QuizLesson) => QuizLesson): QuizContent {
  let changed = false;
  const lessons = content.lessons.map((u) => {
    let unitChanged = false;
    const drills = u.drills.map((d) => {
      const next = fn(d);
      if (next !== d) unitChanged = true;
      return next;
    });
    if (!unitChanged) return u;
    changed = true;
    return { ...u, drills };
  });
  return changed ? { ...content, lessons } : content;
}

function replaceById<T extends { id: string }>(list: T[], id: string, fn: (x: T) => T): T[] | null {
  const i = list.findIndex((x) => x.id === id);
  if (i < 0) return null;
  const next = list.slice();
  next[i] = fn(list[i]);
  return next;
}

export type QuestionPatch = Partial<
  Pick<
    QuizQuestion,
    | 'question_text'
    | 'option_a'
    | 'option_b'
    | 'option_c'
    | 'option_d'
    | 'explanation'
    | 'correct_explanation'
    | 'correct_option'
    | 'blank_accepted_variants'
  >
>;

/** The editable columns of a whole question — feat-300's Apply returns the
 *  updated question, and it is patched in through `patchQuestion` like any
 *  inline save (one patch path, so the two can't diverge). Keys the server left
 *  out are left out here too; `translations` is never part of a patch. */
export const QUESTION_PATCH_KEYS = [
  'question_text',
  'option_a',
  'option_b',
  'option_c',
  'option_d',
  'explanation',
  'correct_explanation',
  'correct_option',
  'blank_accepted_variants',
] as const;

export function questionPatchFrom(q: QuizQuestion): QuestionPatch {
  const out: Record<string, unknown> = {};
  for (const k of QUESTION_PATCH_KEYS) if (q[k] !== undefined) out[k] = q[k];
  return out as QuestionPatch;
}

export function patchQuestion(content: QuizContent, id: string, patch: QuestionPatch): QuizContent {
  return mapDrills(content, (d) => {
    const next = replaceById(d.question_items, id, (q) => ({ ...q, ...patch }));
    return next ? { ...d, question_items: next } : d;
  });
}

export type ReadingPatch = Partial<Pick<ReadingItemSummary, 'title' | 'body'>>;

export function patchReading(content: QuizContent, id: string, patch: ReadingPatch): QuizContent {
  return mapDrills(content, (d) => {
    if (!d.reading_items) return d;
    const next = replaceById(d.reading_items, id, (r) => ({ ...r, ...patch }));
    return next ? { ...d, reading_items: next } : d;
  });
}

export type RehearsalPatch = Partial<Pick<RehearsalLessonItem, 'name' | 'scenario_text'>> & {
  criteria?: RehearsalCriterion[];
};

export function patchRehearsal(content: QuizContent, id: string, patch: RehearsalPatch): QuizContent {
  return mapDrills(content, (d) => {
    const next = replaceById(d.rehearsal_items, id, (r) => {
      const merged = { ...r, ...patch };
      if (patch.criteria) {
        // The sent array is the loaded one minus each entry's `translations`
        // overlay, in the same order — put the overlays back by position.
        merged.criteria = patch.criteria.map((c, i) => {
          const before = r.criteria[i];
          return before?.translations ? { ...c, translations: before.translations } : c;
        });
      }
      return merged;
    });
    return next ? { ...d, rehearsal_items: next } : d;
  });
}

export type UnitPatch = Partial<Pick<QuizUnit, 'title' | 'subtitle'>>;

export function patchUnit(content: QuizContent, id: string, patch: UnitPatch): QuizContent {
  const next = replaceById(content.lessons, id, (u) => ({ ...u, ...patch }));
  return next ? { ...content, lessons: next } : content;
}

export type DrillPatch = Partial<Pick<QuizLesson, 'title'>>;

export function patchDrill(content: QuizContent, id: string, patch: DrillPatch): QuizContent {
  return mapDrills(content, (d) => (d.id === id ? { ...d, ...patch } : d));
}

/** One criterion-field edit, applied at send time (QA-F1). `index` is the
 *  position in the FULL criteria array (goods and bads together). */
export interface CriterionEdit {
  index: number;
  field: 'label' | 'description';
  value: string;
}

/**
 * The FULL criteria array for `updateRehearsal(id, {criteria})` with only one
 * entry's `label` or `description` changed (slice §4, AT-5).
 *
 * The backend replaces the whole set (`_replace_criteria_sql`: DELETE all, then
 * INSERT what is sent, keeping `id` when present), so every entry goes back with
 * its `id`, `kind`, `label`, `description`, `example_phrases` and `position`
 * exactly as loaded. Dropping a field here silently erases data. The only thing
 * removed is the read-side `translations` overlay, which is not a column.
 */
export function criteriaWithEdit(
  criteria: RehearsalCriterion[],
  index: number,
  field: 'label' | 'description',
  value: string,
): RehearsalCriterion[] {
  return criteria.map((c, i) => {
    const { translations: _overlay, ...columns } = c;
    return i === index ? { ...columns, [field]: value } : columns;
  });
}
